<template>
  <div>
    <refresh-alert-dialog
      :visible.sync="refreshAlertVisible"
      :refresh-page-at="refreshPageAt"
      :remote-version="remoteVersion"
      @click:refresh="refreshPage"
      @click:abort="abortPageRefresh"
    />

    <el-container>
      <el-header class="header layout-width-default">
        <nav v-if="user">
          <el-menu ref="menu" :default-active="$route.path" mode="horizontal" router>
            <el-menu-item index="/" v-t="'views.home.title'"/>
            <el-menu-item
              index="/manufacturing-items"
              v-t="'entities.manufacturingItem.name_plural'"
            />

            <el-submenu v-if="userStatuses.length > 1" index="stations">
              <template slot="title">{{ $t('views.header.nav.station') }}</template>
              <template v-for="status in userStatuses">
                <el-menu-item
                  :key="`menu-item-status-${status.id}`"
                  :index="`/stations/${status.station}`"
                >
                <span v-t="{
                  path: getI18nValueKeys('status', 'station', status.station),
                  args: {context: 'short'}
                }"></span>
                </el-menu-item>
              </template>
            </el-submenu>

            <template v-else-if="userStatuses.length === 1">
              <el-menu-item :index="`/stations/${userStatuses[0].station}`">
                <span v-t="{
                  path: getI18nValueKeys('status', 'station', userStatuses[0].station),
                }"></span>
              </el-menu-item>
            </template>

            <el-submenu v-if="isSystemAdmin" index="manage">
              <template slot="title">{{ $t('views.header.nav.manage') }}</template>

              <el-menu-item index="/users" v-t="'entities.user.name_plural'"/>
              <el-menu-item index="/manufacturers" v-t="'entities.manufacturer.name_plural'"/>
              <el-menu-item index="/statuses" v-t="'entities.status.name_plural'"/>
              <el-menu-item index="/locales" v-t="'entities.locale.name_plural'"/>
              <el-menu-item index="/set-elements" v-t="'entities.setElement.name_plural'"/>
            </el-submenu>

            <el-menu-item
              v-if="user"
              :index="null"
              @click="logOut"
              class="float-right"
            >
              <span v-t="{path: 'views.header.nav.logOut', args: {username: user.username}}"></span>
            </el-menu-item>

            <el-submenu index="language" class="float-right">
              <template slot="title">
                <i
                  v-if="languageOptions[language]"
                  :class="`border flag-icon flag-icon-${languageOptions[language].icon}`"
                ></i>
                <template v-else>{{ language }}</template>
              </template>
              <el-menu-item
                v-for="lang in Object.keys(languageOptions)"
                :key="lang"
                :index="null"
                :value="lang"
                @click="changeLanguage(lang)"
                :class="{'is-active': lang === language}"
              >
                <i :class="`mr-1 border flag-icon flag-icon-${languageOptions[lang].icon}`"></i>
                <span v-t="`common.languages.${lang}`"></span>
                <span v-t="{path: `common.languages.${lang}`, args: { lng: lang } }"
                  class="float-right text-muted font-italic"></span>
              </el-menu-item>
            </el-submenu>
          </el-menu>
        </nav>
      </el-header>

      <el-main :class="`main layout-width-${layoutWidthMode}`">
        <router-view :key="language" @requestLayoutWidthMode="onRequestLayoutWidthMode"/>
      </el-main>

      <el-footer :class="`layout-width-${layoutWidthMode}`">
        <div class="border-top"></div>

        <p class="text-secondary text-right" style="font-size: .75rem">
          {{ version }}
        </p>
      </el-footer>
    </el-container>
  </div>
</template>

<script>
import { CronJob } from 'cron';
import moment, { duration } from 'moment';
import { mapGetters, mapState } from 'vuex';
import RefreshAlertDialog from '@/components/RefreshAlertDialog.vue';
import config from '@/config';
import { changeDxLocale } from '@/lib/dx/localization';
import { changeMomentLocale } from '@/lib/moment/localization';
import { getI18nValueKeys } from '@/lib/util';

export default {
  name: 'App',
  components: {
    RefreshAlertDialog,
  },
  data() {
    return {
      version: config.version,
      remoteVersion: config.version,
      language: this.$i18n.i18next.language.split('-')[0],
      languageOptions: {
        en: { icon: 'gb' },
        de: { icon: 'de' },
        pl: { icon: 'pl' },
      },
      layoutWidthMode: 'default',
      checkVersionCronExpression: '*/30 * * * *',
      checkVersionJob: null,
      refreshPageCronExpression: '45 1 * * *',
      refreshAlertVisible: false,
      refreshPageJob: null,
      refreshPageTimeoutHandle: null,
      refreshPageTimeout: duration(1, 'minute')
        .asMilliseconds(),
      refreshPageAt: null,
      refreshPageCountDownIntervalHandle: null,
    };
  },
  computed: {
    ...mapState(['user']),
    ...mapGetters(['isSystemAdmin', 'userStatuses']),
  },
  watch: {
    '$route.path': function onRoutePath() {
      this.layoutWidthMode = 'default';
    },
  },
  methods: {
    getI18nValueKeys,
    async logOut() {
      await this.$store.dispatch('logOut');
      this.$router.push('/login');
    },
    async checkVersion() {
      const { protocol, hostname, port } = window.location;

      const response = await this.$http.get('/version.json', {
        baseURL: `${protocol}//${hostname}:${port}`,
      });

      this.remoteVersion = response.data.version;

      if (this.remoteVersion === this.version) return;

      this.schedulePageRefresh();
    },
    refreshPage() {
      window.location.reload();
    },
    abortPageRefresh() {
      if (this.refreshPageTimeoutHandle) {
        clearTimeout(this.refreshPageTimeoutHandle);
        this.refreshPageTimeoutHandle = null;
      }
    },
    schedulePageRefresh() {
      this.refreshPageTimeoutHandle = setTimeout(this.refreshPage, this.refreshPageTimeout);
      this.refreshAlertVisible = true;
      this.refreshPageAt = moment()
        .add(this.refreshPageTimeout, 'ms')
        .toDate();
    },
    async changeLanguage(language) {
      await this.$i18n.i18next.changeLanguage(language);
    },
    onLanguageChanged(language) {
      // eslint-disable-next-line prefer-destructuring
      this.language = language.split('-')[0];

      changeDxLocale(language);
      changeMomentLocale(language);
    },
    onRequestLayoutWidthMode(mode) {
      this.layoutWidthMode = mode;
    },
  },
  mounted() {
    if (config.checkVersion) {
      this.checkVersionJob = new CronJob(this.checkVersionCronExpression, this.checkVersion);
      this.checkVersionJob.start();
    }

    this.refreshPageJob = new CronJob(this.refreshPageCronExpression, this.schedulePageRefresh);
    this.refreshPageJob.start();

    this.onLanguageChanged(this.$i18n.i18next.language);
    this.$i18n.i18next.on('languageChanged', this.onLanguageChanged);
  },
  beforeDestroy() {
    this.$i18n.i18next.off('languageChanged', this.onLanguageChanged);

    if (config.checkVersion) {
      this.checkVersionJob.stop();
    }

    this.refreshPageJob.stop();
    this.abortPageRefresh();
  },
};
</script>

<style lang="sass">
  $layout-width-default: 1200px

  body
    margin: 0
    font-family: 'Avenir', Helvetica, Arial, sans-serif
    -webkit-font-smoothing: antialiased
    -moz-osx-font-smoothing: grayscale
    color: #2c3e50

  @media (min-width: $layout-width-default)
    .layout-width-default
      margin: auto
      width: $layout-width-default
</style>
